import React, { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { ChatContextSourceType } from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import { DepartmentChip } from 'src/pages/Controls/DepartmentChip'
import {
  ChatContext,
  ChatContextSelection,
  useChatContextPolicies,
} from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import { generatePermalinkFromId } from 'src/components/PageLayout/Permalink/generatePermalink'
import { MultiSelectModal } from 'src/components/Reusable/MultiSelectModal'
import { GridRowLinkItem } from '../chatContextPanel.components'
import {
  ChipContainer,
  ContextSelectorTableText,
} from '../chatContextPanel.styles'

export type PolicyModalProps = {
  show: boolean
  hide: () => void
  onBack?: () => void
  context: ChatContext
  setContext: (context: ChatContext) => void
}

export const PolicyModal = ({
  show,
  hide,
  onBack,
  context,
  setContext,
}: PolicyModalProps): JSX.Element => {
  const currentSelection = useMemo(() => {
    return context[ChatContextSourceType.POLICY]
  }, [context])
  const { data, isLoading, error } = useChatContextPolicies()
  const params = useParams()

  const items = useMemo(() => {
    return (
      data?.getItemsList()?.map((policy) => ({
        id: policy.getId(),
        renderItems: [
          <ContextSelectorTableText key={`${policy.getId()}-name`}>
            <GridRowLinkItem
              link={generatePermalinkFromId({
                pageContext: params.pageContext as string,
                modelType: MODEL_TYPE.POLICY,
                modelId: policy.getModelId(),
                isInternalLink: false,
              })}
              text={policy.getName()}
            />
          </ContextSelectorTableText>,
          <ChipContainer key={`${policy.getId()}-department`}>
            <DepartmentChip departmentId={policy.getDepartmentId()} />
          </ChipContainer>,
        ],
      })) ?? []
    )
  }, [data, params.pageContext])

  const onCommit = useCallback(
    (newSelection: ChatContextSelection) => {
      setContext({
        ...context,
        [ChatContextSourceType.POLICY]: newSelection,
      })
    },
    [context, setContext],
  )

  return (
    <MultiSelectModal
      title="Select Policies"
      description="The AI agent will only consider the policies you select here during your conversation."
      itemType="policy"
      currentSelection={currentSelection}
      onCommit={onCommit}
      show={show}
      hide={hide}
      onBack={onBack}
      isLoading={isLoading}
      items={items}
      gridTemplateColumnStyling="auto auto"
      numberOfColumns={2}
      error={error}
    />
  )
}
